import { LinksList } from '../../../ad-overview/widgets/link-list/LinksList'
import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import React, { useEffect } from 'react'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import { IconUpload } from '../../../../shared/components/icons/IconUpload'
import {
  useExportAffiliateAds,
  useExportLastAd,
  useGetAffiliateLastAdQuery,
} from '../../../../app/api'
import { IPpcComplianceFilter } from '../../model/types/PpcComplianceFilter.interface'
import { selectPpcComplianceFilter } from '../../model/ppcComplianceSlice'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import styles from './LastAdReport.module.scss'
import { Tooltip } from 'antd'
import { IconVerified } from '../../../../shared/components/icons/IconVerified'

export interface IPpcComplianceLastAdProps {
  id: number
  adsCount: number
  affiliate: string
}

export const LastAdReport = ({ id, adsCount, affiliate }: IPpcComplianceLastAdProps) => {
  const {
    project,
    country,
    network,
    dateRange,
    site,
    engine,
    browser,
    city,
    hidden,
    adHijackers,
    shoppingAds,
    status,
    keyword,
    advertiserName,
    reversed_filters,
  } = useAppSelector<IPpcComplianceFilter>(selectPpcComplianceFilter)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const lastAdParams = {
    id,
    projectId: (project ? Number(project) : undefined) as number,
    countryId: country || undefined,
    date: dateRange,
    site: site,
    engineId: engine || undefined,
    browserId: browser || undefined,
    cityId: city || undefined,
    isHijack: adHijackers,
    showShoppingAds: shoppingAds,
    keyword,
    advertiserId: advertiserName || undefined,
    reversedFilters: reversed_filters || undefined,
  }

  const { data, isLoading, isFetching, isError } = useGetAffiliateLastAdQuery(lastAdParams)
  const [exportLastAd, { isFetching: lastAdLoading }] = useExportLastAd()
  const [exportAffiliateAds, { isFetching: affiliateAdsLoading }] = useExportAffiliateAds()

  useEffect(() => {
    isError && dispatch(hideModal())
  }, [isError])

  if (isLoading || isFetching) {
    return (
      <div className={clsx(styles.preloaderWrapper)}>
        <Preloader />
      </div>
    )
  }

  if (!data?.data?.items.length) {
    return (
      <div className={clsx(styles.emptyState)}>
        <svg
          fill='none'
          height='85'
          viewBox='0 0 78 85'
          width='78'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M30 77.0004L35.892 80.2736C37.0264 80.904 37.5936 81.2188 38.1944 81.3424C38.726 81.452 39.274 81.452 39.806 81.3424C40.4064 81.2188 40.9736 80.904 42.108 80.2736L48 77.0004M12 67.0004L6.29188 63.8292C5.09384 63.1636 4.49472 62.8308 4.05852 62.3572C3.67264 61.9388 3.3806 61.4424 3.20192 60.9016C3 60.2904 3 59.6052 3 58.2344V52.0004M3 32.0004V25.7661C3 24.3956 3 23.7103 3.20192 23.0991C3.3806 22.5584 3.67264 22.062 4.05852 21.6433C4.49472 21.1699 5.0938 20.8371 6.29188 20.1715L12 17.0004M30 7.00032L35.892 3.72708C37.0264 3.09684 37.5936 2.78172 38.1944 2.65816C38.726 2.54884 39.274 2.54884 39.806 2.65816C40.4064 2.78172 40.9736 3.09684 42.108 3.72708L48 7.00032M66 17.0003L71.708 20.1715C72.9064 20.8371 73.5052 21.1699 73.9416 21.6433C74.3272 22.062 74.6196 22.5584 74.798 23.0991C75 23.7103 75 24.3956 75 25.7661V32.0003M75 52.0004V58.2344C75 59.6052 75 60.2904 74.798 60.9016C74.6196 61.4424 74.3272 61.9388 73.9416 62.3572C73.5052 62.8308 72.9064 63.1636 71.708 63.8292L66 67.0004M30 37.0004L39 42.0004M39 42.0004L48 37.0004M39 42.0004V52.0004M3 22.0003L12 27.0003M66 27.0003L75 22.0003M39 72.0004V82.0004'
            stroke='#6684A7'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='5'
          />
        </svg>
        <span className={clsx(styles.emptyStateTitle)}>No Results Found</span>
      </div>
    )
  }

  return (
    <div className={clsx(styles.root)}>
      <h3 className={clsx(styles.header)}>
        <span className={clsx(styles.titleText)}>
          {t('pages.PPCCompliance.latestAdFromAffiliate')} {data?.data?.items[0].affiliate}
        </span>
      </h3>
      <div className={clsx(styles.content)}>
        <div className={clsx(styles.contentRow)}>
          <div className={clsx(styles.adCopyBlock)}>
            <h4 className={clsx(styles.adCopyTitle)}>
              {t('pages.PPCCompliance.adCopy')}{' '}
              {data?.data?.items[0].is_shopping_ad && '(shopping ads)'}
            </h4>
            <div className={clsx(styles.adCopyBox)}>
              {data?.data?.items[0].shopping_ad_preview_url && (
                <div className={clsx(styles.adCopyImage)}>
                  <img
                    className={clsx(styles.adCopyImageImg)}
                    src={data.data.items[0].shopping_ad_preview_url}
                  />
                </div>
              )}
              <div>
                <p className={clsx(styles.adTitle)}>{data?.data?.items[0].title}</p>
                <p className={clsx(styles.adText)}>{data?.data?.items[0].text}</p>
                <p className={clsx(styles.adDomain)}>{data?.data?.items[0].finish_domain}</p>
              </div>
            </div>
          </div>
          <div className={clsx(styles.detailsBlock)}>
            <h4 className={clsx(styles.detailsTitle)}>{t('pages.PPCCompliance.adDetails')}</h4>
            <div className={clsx(styles.detailsText)}>
              <div>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.found')}:</span>{' '}
                {data?.data?.items[0].found}
              </div>
              <div className={'flex gap-[6px]'}>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.advertiser')}:</span>
                {data?.data?.items[0].advertiser ? (
                  <div className={'flex w-fit gap-[4px] overflow-hidden'}>
                    <Tooltip title={data?.data?.items[0].advertiser.name}>
                      <p className={'truncate'}>{data?.data?.items[0].advertiser.name}</p>
                    </Tooltip>
                    {data?.data?.items[0].advertiser.verified && (
                      <Tooltip
                        title={t('pages.paidResults.tooltipVerifiedByFallback', {
                          name: data?.data?.items[0].advertiser.verified_by,
                        })}
                      >
                        <span>
                          <IconVerified className='ml-1 mb-1 inline w-[16px]' color='#00BD7B' />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                ) : (
                  <div>N/A</div>
                )}
              </div>
              <div>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.searchEngine')}:</span>{' '}
                {data?.data?.items[0].engine}
              </div>
              <div>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.keyword')}:</span>{' '}
                {data?.data?.items[0].keyword}
              </div>
              <div>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.device')}:</span>{' '}
                {data?.data?.items[0].device}
              </div>
              <div>
                <span className={clsx(styles.detailLabel)}>{t('table.columns.country')}:</span>{' '}
                {data?.data?.items[0].country}
              </div>
              <div className={clsx(styles.detailsLinks)}>
                <BpAnchor href={data?.data?.items[0].search_screen_url} target='_blank'>
                  {t('pages.PPCCompliance.SERPScreenshot')}
                </BpAnchor>
                <BpAnchor href={data?.data?.items[0].link_screen_url} target='_blank'>
                  {t('pages.PPCCompliance.landingPageScreenshot')}
                </BpAnchor>
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(styles.linksSection)}>
          <LinksList
            additionalRedirects={data?.data?.items[0].additionalRedirects}
            className={clsx(styles.linksListNoShadow)}
            redirects={data?.data?.items[0].redirects}
          />
          <p className={clsx(styles.affiliateText)}>{affiliate}</p>
        </div>
      </div>
      <div className={clsx(styles.footer)}>
        <BpButton
          className={clsx(styles.button)}
          iconLeft={<IconUpload />}
          loading={lastAdLoading}
          onClick={() => exportLastAd(lastAdParams)}
        >
          {t('buttons.exportLatestAdToPDF')}
        </BpButton>
        <BpButton
          className={clsx(styles.button)}
          iconLeft={<IconUpload />}
          loading={affiliateAdsLoading}
          onClick={() =>
            exportAffiliateAds({
              projectId: project ? Number(project) : undefined,
              countryId: country || undefined,
              affiliateId: id,
              advertiserId: advertiserName || undefined,
              reversedFilters: reversed_filters || undefined,
              network: network,
              date: dateRange,
              site: site,
              engineId: engine || undefined,
              browserId: browser || undefined,
              cityId: city || undefined,
              isHidden: hidden,
              isHijack: adHijackers,
              isShoppingAds: shoppingAds,
              statusId: status ? Number(status) : undefined,
              export: 'xlsx',
            })
          }
        >
          {t('buttons.exportCountAdsToXls', { count: adsCount })}
        </BpButton>
      </div>
    </div>
  )
}
